import { useEffect, useRef } from "react";
import NoSleep from "nosleep.js";

const useWakeLock = () => {
  const noSleepInstance = useRef(null);
  const wakeLockRef = useRef(null);

  const enableWakeLock = async () => {
    try {
      await noSleepInstance.current.enable();

      try {
        if ("wakeLock" in navigator) {
          wakeLockRef.current = await navigator.wakeLock.request("screen");
          wakeLockRef.current.addEventListener("release", () => {
            console.log("Wake Lock released, attempting to reacquire...");
            setTimeout(enableWakeLock, 1000);
          });
        }
      } catch (err) {
        console.log(
          "Wake Lock API not supported or permission denied, falling back to NoSleep.js"
        );
      }

      console.log("Continuous monitoring enabled");
    } catch (err) {
      console.error("Failed to enable continuous monitoring:", err);
      setTimeout(enableWakeLock, 5000);
    }
  };

  const reacquireWakeLock = async () => {
    try {
      await noSleepInstance.current.enable();

      if ("wakeLock" in navigator && !wakeLockRef.current) {
        wakeLockRef.current = await navigator.wakeLock.request("screen");
      }
    } catch (err) {
      console.error("Error reacquiring wake lock:", err);
    }
  };

  const disableWakeLock = () => {
    if (noSleepInstance.current) {
      try {
        noSleepInstance.current.disable();
      } catch (err) {
        console.error("Error disabling NoSleep:", err);
      }
    }

    if (wakeLockRef.current) {
      try {
        wakeLockRef.current.release();
      } catch (err) {
        console.error("Error releasing wake lock:", err);
      }
      wakeLockRef.current = null;
    }
  };

  useEffect(() => {
    noSleepInstance.current = new NoSleep();
    enableWakeLock();

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        reacquireWakeLock();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      disableWakeLock();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  return {
    enableWakeLock,
    reacquireWakeLock,
    disableWakeLock,
  };
};

export default useWakeLock;
